import React, { useMemo, lazy, Suspense, useState, useEffect, useRef, useCallback } from "react";
import { Container, Row, Col } from "react-bootstrap";
import { motion } from "framer-motion";
import Typewriter from "typewriter-effect";
import ServiceIcon from "./ServiceIcon";
import { staggerContainer, ctaEntry, sectionReveal, fadeInUp, easing } from "../../utils/motion";
import { Link } from "react-router-dom";
import { getSelectedWorkProjects } from "../../services/projectService";
import { HomeSEO } from "../SEOHead";

// Lazy load heavy Lottie component - only loads after initial paint
const GreetingLottie = lazy(() => import("../DisplayLottie"));

// Services data - memoized outside component to prevent re-creation
const services = [
    {
        title: "Web Application Development",
        description: "Scalable, production-ready web applications built with modern frameworks.",
        icon: "web",
    },
    {
        title: "Mobile App Development",
        description: "Cross-platform mobile solutions for iOS and Android.",
        icon: "mobile",
    },
    {
        title: "Backend & API Engineering",
        description: "Robust backend systems and APIs designed for scale.",
        icon: "backend",
    },
    {
        title: "Cloud & DevOps",
        description: "Infrastructure automation and cloud deployment solutions.",
        icon: "cloud",
    },
    {
        title: "UI/UX & Performance Optimization",
        description: "Motion-first interfaces and performance optimization.",
        icon: "uiux",
    },
];

// Tech Stack grouped by category
const techStack = {
    frontend: ["React", "Next.js", "TypeScript", "Tailwind CSS", "Framer Motion"],
    backend: ["Node.js", "Express", "PostgreSQL", "MongoDB", "GraphQL"],
    mobile: ["Flutter", "React Native", "Swift", "Dart"],
    cloud: ["AWS", "Docker", "CI/CD", "Kubernetes", "Firebase"],
};

// Selected Work projects - now fetched dynamically (moved inside component)

// Process steps
const processSteps = [
    { step: 1, title: "Discovery", description: "Understanding your vision and requirements." },
    { step: 2, title: "Planning", description: "Technical specifications and architecture design." },
    { step: 3, title: "Design", description: "UI/UX and motion design systems." },
    { step: 4, title: "Development", description: "Clean code and best practices implementation." },
    { step: 5, title: "Launch", description: "Deployment and performance monitoring." },
    { step: 6, title: "Support", description: "Ongoing maintenance and optimization." },
];

// Combined scroll reveal hook using a single IntersectionObserver for better performance
const useCombinedScrollReveal = (sectionIds) => {
    const [visibleSections, setVisibleSections] = useState({});
    const refs = useRef({});
    const observerRef = useRef(null);
    const hasAnimatedRef = useRef({});

    const setRef = useCallback((id) => (el) => {
        refs.current[id] = el;
    }, []);

    useEffect(() => {
        // Create a single observer for all sections
        observerRef.current = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    const id = entry.target.dataset.sectionId;
                    if (entry.isIntersecting && !hasAnimatedRef.current[id]) {
                        hasAnimatedRef.current[id] = true;
                        setVisibleSections((prev) => ({ ...prev, [id]: true }));
                    }
                });
            },
            { threshold: 0.1, rootMargin: '0px' }
        );

        // Observe all sections
        Object.entries(refs.current).forEach(([id, el]) => {
            if (el) {
                el.dataset.sectionId = id;
                observerRef.current.observe(el);
            }
        });

        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect();
            }
        };
    }, []);

    return { setRef, visibleSections };
};

// Memoize Home2 to prevent unnecessary re-renders
const Home2 = React.memo(function Home2() {
    const { setRef, visibleSections } = useCombinedScrollReveal([
        'hero', 'services', 'capabilities', 'work', 'process', 'cta'
    ]);

    // Track if this is the first mount (for animations)
    const [isMounted, setIsMounted] = useState(false);
    const [lottieData, setLottieData] = useState(null);
    const lottieLoadedRef = useRef(false);
    const [selectedWork, setSelectedWork] = useState([]);

    useEffect(() => {
        // Mark as mounted immediately for instant display
        setIsMounted(true);

        // Load Lottie data only once (persists across hide/show)
        if (!lottieLoadedRef.current) {
            lottieLoadedRef.current = true;
            // Use requestIdleCallback for non-blocking load
            const loadLottie = () => {
                import("../../Assets/lottie/coding.json").then((module) => {
                    setLottieData(module.default);
                });
            };

            if ('requestIdleCallback' in window) {
                requestIdleCallback(loadLottie);
            } else {
                setTimeout(loadLottie, 50);
            }
        }
    }, []);

    // Fetch selected work projects
    useEffect(() => {
        const fetchSelectedWork = async () => {
            try {
                const projects = await getSelectedWorkProjects();
                // Transform projects to match the expected format for selected work display
                const transformedProjects = projects.map(project => ({
                    name: project.title,
                    description: project.description,
                    tech: [], // Could be populated from project data if needed
                    imgPath: project.imgPath,
                    demoLink: project.demoLink
                }));
                setSelectedWork(transformedProjects);
            } catch (error) {
                console.error('Failed to fetch selected work projects:', error);
                // Fallback to empty array if fetch fails
                setSelectedWork([]);
            }
        };

        fetchSelectedWork();
    }, []);

    // Memoize Typewriter options to prevent re-creation on every render
    const typewriterOptions = useMemo(() => ({
        strings: [
            "High-Performance Web & Mobile Products",
            "Scalable Digital Solutions",
            "Production-Ready Applications",
            "Modern Tech Experiences"
        ],
        autoStart: true,
        loop: true,
        deleteSpeed: 50,
        delay: 75,
        cursor: "|",
        wrapperClassName: "typewriter-wrapper",
        cursorClassName: "typewriter-cursor"
    }), []);

    // Parallax removed for better performance - using CSS only

    return (
        <>
        <HomeSEO />
        <div className="homepage-wrapper">
            {/* Hero Section */}
            <section className="hero-section" id="home" ref={setRef('hero')}>
                <Container>
                    <Row className="align-items-center">
                        <Col lg={7} md={12} className="hero-text-col">
                            {/* Hero content with CSS animations - non-blocking */}
                            <div className={`hero-content ${isMounted ? 'hero-animate-in' : ''}`}>
                                <h1 className="hero-headline hero-fade-up" style={{ animationDelay: '0s' }}>
                                    <span className="gradient-text">We Build</span>
                                    <br />
                                    <Typewriter
                                        options={typewriterOptions}
                                    />
                                </h1>

                                <p className="hero-subheading hero-fade-up" style={{ animationDelay: '0.1s' }}>
                                    A full-stack development agency delivering scalable, production-ready digital solutions.
                                </p>

                                <div className="hero-cta-group hero-fade-up" style={{ animationDelay: '0.2s' }}>
                                    <Link to="/contact" className="cta-primary">
                                        <span>Start a Project</span>
                                        <span className="cta-arrow">→</span>
                                    </Link>
                                    <Link to="/work" className="cta-secondary">
                                        View Work
                                    </Link>
                                </div>
                            </div>
                        </Col>

                        <Col lg={5} md={12} className="hero-animation-col">
                            <div className={`hero-lottie-wrapper ${isMounted ? 'hero-lottie-animate-in' : ''}`}>
                                <div className="lottie-glow-effect"></div>
                                {lottieData && (
                                    <Suspense fallback={<div className="lottie-placeholder" />}>
                                        <GreetingLottie animationData={lottieData} />
                                    </Suspense>
                                )}
                            </div>
                        </Col>
                    </Row>
                </Container>
                <div className="hero-background"></div>
            </section>

            {/* Services Overview */}
            <section className="services-section" id="services" ref={setRef('services')}>
                {/* Static Background Elements - CSS only for performance */}
                <div className="services-background">
                    <div className="tech-grid-pattern"></div>
                    <div className="circuit-lines"></div>
                    {/* Floating particles removed for performance - using CSS animation instead */}
                    <div className="floating-particles-css"></div>
                </div>

                <Container>
                    <motion.div
                        variants={sectionReveal}
                        initial="hidden"
                        animate={visibleSections.services ? "visible" : "hidden"}
                    >
                        <h2 className="section-title">
                            <span className="section-title-text">What We Do</span>
                            <span className="section-title-underline"></span>
                        </h2>
                    </motion.div>

                    <motion.div
                        variants={staggerContainer}
                        initial="hidden"
                        animate={visibleSections.services ? "visible" : "hidden"}
                    >
                        <Row>
                            {services.map((service, index) => (
                                <Col md={6} lg={4} key={index} className="service-card-wrapper">
                                    <motion.div
                                        className="service-card"
                                        variants={fadeInUp}
                                        custom={index}
                                        whileHover={{
                                            y: -8,
                                            scale: 1.03,
                                            rotateY: 2,
                                        }}
                                        transition={{ duration: 0.4, ease: easing.primary, type: "tween" }}
                                    >
                                        {/* Card Background Glow */}
                                        <div className="service-card-glow"></div>

                                        {/* Animated Border */}
                                        <div className="service-card-border"></div>

                                        {/* Icon Container */}
                                        <motion.div
                                            className="service-card-icon-wrapper"
                                            whileHover={{
                                                scale: 1.1,
                                                rotateZ: [0, -5, 5, 0],
                                            }}
                                            transition={{ duration: 0.5, ease: easing.primary }}
                                        >
                                            <ServiceIcon type={service.icon} className="service-icon-svg" />
                                            <div className="icon-pulse-ring"></div>
                                        </motion.div>

                                        <h3 className="service-card-title">{service.title}</h3>
                                        <p className="service-card-description">{service.description}</p>

                                        {/* Animated Accent Line */}
                                        <div className="service-card-accent"></div>

                                        {/* Tech Pattern Overlay */}
                                        <div className="service-card-pattern"></div>
                                    </motion.div>
                                </Col>
                            ))}
                        </Row>
                    </motion.div>
                </Container>
            </section>

            {/* Capabilities / Tech Stack */}
            <section className="capabilities-section" ref={setRef('capabilities')}>
                <Container>
                    <motion.div
                        variants={sectionReveal}
                        initial="hidden"
                        animate={visibleSections.capabilities ? "visible" : "hidden"}
                    >
                        <h2 className="section-title">
                            <span className="section-title-text">Our Capabilities</span>
                            <span className="section-title-underline"></span>
                        </h2>
                    </motion.div>

                    <motion.div
                        variants={staggerContainer}
                        initial="hidden"
                        animate={visibleSections.capabilities ? "visible" : "hidden"}
                    >
                        <Row>
                            {Object.entries(techStack).map(([category, technologies], categoryIndex) => (
                                <Col md={6} lg={3} key={category} className="tech-category-wrapper">
                                    <motion.div
                                        className="tech-category"
                                        variants={fadeInUp}
                                        custom={categoryIndex}
                                    >
                                        <h3 className="tech-category-title">{category.charAt(0).toUpperCase() + category.slice(1)}</h3>
                                        <div className="tech-items">
                                            {technologies.map((tech, techIndex) => (
                                                <motion.span
                                                    key={tech}
                                                    className="tech-item"
                                                    variants={fadeInUp}
                                                    custom={categoryIndex * 5 + techIndex}
                                                >
                                                    {tech}
                                                </motion.span>
                                            ))}
                                        </div>
                                    </motion.div>
                                </Col>
                            ))}
                        </Row>
                    </motion.div>
                </Container>
            </section>

            {/* Selected Work */}
            <section className="work-section" ref={setRef('work')}>
                <Container>
                    <motion.div
                        variants={sectionReveal}
                        initial="hidden"
                        animate={visibleSections.work ? "visible" : "hidden"}
                    >
                        <h2 className="section-title">
                            <span className="section-title-text">Selected Work</span>
                            <span className="section-title-underline"></span>
                        </h2>
                    </motion.div>

                    <motion.div
                        variants={staggerContainer}
                        initial="hidden"
                        animate={visibleSections.work ? "visible" : "hidden"}
                    >
                        <Row>
                            {selectedWork.map((project, index) => (
                                <Col md={6} lg={4} key={index} className="work-card-wrapper">
                                    {project.demoLink ? (
                                        <Link to={project.demoLink} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
                                            <motion.div
                                                className="work-card"
                                                variants={fadeInUp}
                                                custom={index}
                                                whileHover={{ y: -4 }}
                                                transition={{ duration: 0.3, ease: easing.primary, type: "tween" }}
                                            >
                                                <div className="work-card-image-wrapper">
                                                    <div className="work-card-image">
                                                        {project.imgPath ? (
                                                            <img
                                                                src={project.imgPath}
                                                                alt={project.name}
                                                                onError={(e) => {
                                                                    e.target.style.display = 'none';
                                                                    e.target.nextSibling.style.display = 'block';
                                                                }}
                                                            />
                                                        ) : null}
                                                        <div className="work-card-pattern" style={{ display: project.imgPath ? 'none' : 'block' }}></div>
                                                    </div>
                                                    <div className="work-card-overlay">
                                                        <motion.div
                                                            className="work-card-view-indicator"
                                                            initial={{ opacity: 0, scale: 0.8 }}
                                                            whileHover={{ opacity: 1, scale: 1 }}
                                                            transition={{ duration: 0.3, type: "tween" }}
                                                        >
                                                            View Project →
                                                        </motion.div>
                                                    </div>
                                                </div>
                                                <div className="work-card-content">
                                                    <motion.h3
                                                        className="work-card-title"
                                                        whileHover={{ x: 4 }}
                                                        transition={{ duration: 0.2, type: "tween" }}
                                                    >
                                                        {project.name}
                                                    </motion.h3>
                                                    <p className="work-card-description">{project.description}</p>
                                                    <div className="work-card-tech">
                                                        {project.tech.map((tag, tagIndex) => (
                                                            <motion.span
                                                                key={tagIndex}
                                                                className="tech-tag"
                                                                whileHover={{ scale: 1.05, y: -2 }}
                                                                transition={{ duration: 0.2, type: "tween" }}
                                                            >
                                                                {tag}
                                                            </motion.span>
                                                        ))}
                                                    </div>
                                                </div>
                                            </motion.div>
                                        </Link>
                                    ) : (
                                        <motion.div
                                            className="work-card"
                                            variants={fadeInUp}
                                            custom={index}
                                            whileHover={{ y: -4 }}
                                            transition={{ duration: 0.3, ease: easing.primary, type: "tween" }}
                                        >
                                            <div className="work-card-image-wrapper">
                                                <div className="work-card-image">
                                                    {project.imgPath ? (
                                                        <img src={project.imgPath} alt={project.name} />
                                                    ) : (
                                                        <div className="work-card-pattern"></div>
                                                    )}
                                                </div>
                                                <div className="work-card-overlay">
                                                    <motion.div
                                                        className="work-card-view-indicator"
                                                        initial={{ opacity: 0, scale: 0.8 }}
                                                        whileHover={{ opacity: 1, scale: 1 }}
                                                        transition={{ duration: 0.3, type: "tween" }}
                                                    >
                                                        View Details →
                                                    </motion.div>
                                                </div>
                                            </div>
                                            <div className="work-card-content">
                                                <motion.h3
                                                    className="work-card-title"
                                                    whileHover={{ x: 4 }}
                                                    transition={{ duration: 0.2, type: "tween" }}
                                                >
                                                    {project.name}
                                                </motion.h3>
                                                <p className="work-card-description">{project.description}</p>
                                                <div className="work-card-tech">
                                                    {project.tech.map((tag, tagIndex) => (
                                                        <motion.span
                                                            key={tagIndex}
                                                            className="tech-tag"
                                                            whileHover={{ scale: 1.05, y: -2 }}
                                                            transition={{ duration: 0.2, type: "tween" }}
                                                        >
                                                            {tag}
                                                        </motion.span>
                                                    ))}
                                                </div>
                                            </div>
                                        </motion.div>
                                    )}
                                </Col>
                            ))}
                        </Row>
                    </motion.div>
                </Container>
            </section>

            {/* Process Overview */}
            <section className="process-section" ref={setRef('process')}>
                <Container>
                    <motion.div
                        variants={sectionReveal}
                        initial="hidden"
                        animate={visibleSections.process ? "visible" : "hidden"}
                    >
                        <h2 className="section-title">
                            <span className="section-title-text">Our Process</span>
                            <span className="section-title-underline"></span>
                        </h2>
                    </motion.div>

                    <motion.div
                        className="process-container"
                        variants={staggerContainer}
                        initial="hidden"
                        animate={visibleSections.process ? "visible" : "hidden"}
                    >
                        {processSteps.map((step, index) => (
                            <motion.div
                                key={step.step}
                                className="process-step-item"
                                variants={fadeInUp}
                                custom={index}
                                whileHover={{ x: 8 }}
                                transition={{ duration: 0.3, type: "tween" }}
                            >
                                <motion.div
                                    className="process-step-number"
                                    whileHover={{ rotate: 360 }}
                                    transition={{ duration: 0.6, ease: easing.primary, type: "tween" }}
                                >
                                    {step.step}
                                </motion.div>
                                <div className="process-step-content">
                                    <h3 className="process-step-title">{step.title}</h3>
                                    <p className="process-step-description">{step.description}</p>
                                </div>
                                {index < processSteps.length - 1 && (
                                    <motion.div
                                        className="process-step-connector"
                                        initial={{ scaleY: 0 }}
                                        animate={visibleSections.process ? { scaleY: 1 } : { scaleY: 0 }}
                                        transition={{ delay: index * 0.1 + 0.3, duration: 0.5, ease: easing.primary, type: "tween" }}
                                    />
                                )}
                            </motion.div>
                        ))}
                    </motion.div>
                </Container>
            </section>

            {/* Primary CTA Section */}
            <section className="cta-section" ref={setRef('cta')}>
                <Container>
                    <motion.div
                        className="cta-content"
                        variants={ctaEntry}
                        initial="hidden"
                        animate={visibleSections.cta ? "visible" : "hidden"}
                    >
                        <motion.h2
                            className="cta-headline"
                            initial={{ opacity: 0, y: 20 }}
                            animate={visibleSections.cta ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                            transition={{ duration: 0.6, ease: easing.primary, type: "tween" }}
                        >
                            Have an idea? <span className="gradient-text">Let's build it.</span>
                        </motion.h2>
                        <Link to="/contact" className="cta-button-primary">
                            Book a Call
                        </Link>
                    </motion.div>
                </Container>
            </section>
        </div>
        </>
    );
});

export default Home2;